import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, TrendingUp, Sparkles } from "lucide-react";
import api from "@/lib/api";
import GolfWidget from "@/components/GolfWidget";
import { fmtDur } from "@/lib/ui";

const LOAD_CLS = {
  "chargée": "text-rose-400 bg-rose-500/10",
  "équilibrée": "text-sky-400 bg-sky-500/10",
  "légère": "text-emerald-400 bg-emerald-500/10",
};

export default function Week() {
  const [week, setWeek] = useState([]);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/plan/week"), api.get("/review/week")]).then(([w, r]) => {
      setWeek(w.data.week); setReview(r.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-[50vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Ma Semaine</h1>
        <p className="text-muted-foreground mt-1 text-sm">Une organisation recommandée qui protège tes gros blocs de travail — et ton temps libre.</p>
      </div>

      {/* TIME FREED hero */}
      {review && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-card p-7">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-400 mb-2">
            <TrendingUp className="w-3.5 h-3.5" /> Temps libéré cette semaine
          </div>
          <div className="font-display text-5xl font-semibold" data-testid="week-time-freed">{fmtDur(review.time_freed_minutes)}</div>
          <div className="text-sm text-muted-foreground mt-1">Grâce au tri automatique des emails et à la consolidation des blocs de temps.</div>
          <div className="flex flex-wrap gap-8 mt-6">
            {review.metrics.map((m) => (
              <div key={m.label}>
                <div className="font-display text-2xl font-semibold">{m.value}</div>
                <div className="text-xs text-muted-foreground">{m.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Week days */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="week-days">
          {week.map((d, i) => (
            <motion.div key={d.date} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              data-testid={`week-day-${d.day}`}
              className={`rounded-2xl border p-4 ${d.is_today ? "border-primary/50 bg-primary/5" : "border-border bg-card"} ${d.is_past ? "opacity-50" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-semibold">{d.day}</div>
                <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full ${LOAD_CLS[d.load] || "text-muted-foreground bg-muted"}`}>{d.load}</span>
              </div>
              <div className="text-sm text-primary mt-1">{d.theme}</div>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-muted-foreground">
                <span>{d.meetings_count} RDV</span>
                <span>· {fmtDur(d.free_minutes)} libre</span>
                <span>· bloc max {fmtDur(d.biggest_free_block)}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Golf + observations */}
        <div className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-6">
            <GolfWidget compact />
          </div>
          {review && (
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="week-observations">
              <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                <Sparkles className="w-3.5 h-3.5 text-primary" /> Observations
              </div>
              <ul className="space-y-2.5">
                {review.observations.map((o, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-primary">·</span> {o}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
